import type {MetaTrackingContext, TrackingConsent} from './metaTracking';

/** Keep funnel context, never OAuth tokens/codes or user-entered query data. */
export function guestFunnelPath(path: string, search: string): string {
  const incoming = new URLSearchParams(search);
  const query = new URLSearchParams();
  const funnel = incoming.get('qz');
  if (funnel === 'fc1' || funnel === 'tst1') query.set('qz', funnel);
  else if (incoming.get('flow') === 'organic') query.set('flow', 'organic');
  if (incoming.get('entry') === 'identity') query.set('entry', 'identity');
  return `${path}${query.size ? `?${query}` : ''}`;
}

type AnalyticsOptions = {
  consent: () => TrackingConsent;
  search: () => string;
  capture: (event: string, properties: Record<string, unknown>) => void;
  pixel: (event: 'Lead' | 'InitiateCheckout' | 'CompleteRegistration', properties: Record<string, unknown>, eventId: string) => unknown;
  customPixel?: (event: 'GuestStepViewed', properties: {guest_screen:string}) => boolean;
  context: (eventId: string) => MetaTrackingContext;
  receiptStorage?: Pick<Storage,'getItem'|'setItem'>;
};
export function createGuestAnalytics(options: AnalyticsOptions) {
  let lastView: string | undefined;
  const metaViews = new Set<string>();
  const metaScreens = new Set(['research','methods','email','plans','payment']);
  let authenticatedUserId: string | null = null;
  const sessions = new Set<string>();
  const registrations = new Set<string>();
  const leads = new Set<string>();
  const allowed = () => options.consent() === 'accepted'
    && new URLSearchParams(options.search()).get('qz') !== 'tst1';
  const event = (name: string, properties: Record<string, unknown> = {}) => {
    if (allowed()) options.capture(name, {...(authenticatedUserId?{user_id:authenticatedUserId}:{}),...properties, checkout_flow:'guest'});
  };
  return {
    event,
    lead(receipt: {eventId: string | null | undefined}) {
      const id = receipt.eventId;
      if (!allowed() || !id || !/^lead:[a-z0-9-]+$/iu.test(id) || leads.has(id)) return;
      const key = `focuscorp_lead_sent:${id}`;
      try { if (options.receiptStorage?.getItem(key)) return; } catch { /* Provider event-ID dedupe remains available. */ }
      try {
        const sent = options.pixel('Lead', {content_name:'FocusCorp personal plan',status:'saved'}, id);
        if (sent === false) return;
        leads.add(id);
        try { options.receiptStorage?.setItem(key, '1'); } catch { /* Storage is optional. */ }
      } catch { /* Optional analytics must never turn a saved email into a funnel error. */ }
    },
    payment(stage:'ready'|'submitted',plan:string) { event(stage==='ready'?'checkout_embedded_viewed':'checkout_payment_started',{plan}); },
    identify(userId: string | null) { authenticatedUserId=userId; },
    registration(receipt: {eventId:string;userId:string;provider:'email'|'apple'}) {
      if (!allowed() || !/^registration:[a-z0-9-]+$/iu.test(receipt.eventId) || registrations.has(receipt.eventId)) return;
      const key=`focuscorp_registration_sent:${receipt.eventId}`;
      try { if(options.receiptStorage?.getItem(key)) return; } catch { /* In-memory and provider event-ID dedupe still apply. */ }
      registrations.add(receipt.eventId);
      event('account_created',{provider:receipt.provider,user_id:receipt.userId,email_confirmation_required:false,event_id:receipt.eventId,$insert_id:receipt.eventId});
      options.pixel('CompleteRegistration',{content_name:'FocusCorp web account',registration_method:receipt.provider,status:'completed'},receipt.eventId);
      try { options.receiptStorage?.setItem(key,'1'); } catch { /* Optional storage cannot block signup. */ }
    },
    context(eventId: string): MetaTrackingContext {
      return allowed() ? options.context(eventId) : {consent:'rejected',eventId};
    },
    view(page: string) {
      if (!allowed()) return;
      if (metaScreens.has(page) && !metaViews.has(page)) {
        try { if (options.customPixel?.('GuestStepViewed',{guest_screen:page})) metaViews.add(page); }
        catch { /* Ad measurement cannot interrupt the guest checkout. */ }
      }
      if (page === lastView) return;
      lastView = page;
      const view = page === 'plans' ? 'checkout' : page === 'payment' ? 'embeddedCheckout' : `guest_${page}`;
      event('funnel_viewed', {funnel_view:view, guest_screen:page});
    },
    checkout(payment: {sessionId:string;eventId:string;plan:string;value:number}) {
      if (!allowed() || sessions.has(payment.sessionId)) return;
      sessions.add(payment.sessionId);
      const properties = {plan:payment.plan,currency:'EUR',price:payment.value,session_id:payment.sessionId};
      event('checkout_session_created', properties);
      options.pixel('InitiateCheckout', {content_category:'subscription',content_name:payment.plan,currency:'EUR',value:payment.value}, payment.eventId);
    },
  };
}
export type GuestAnalytics = ReturnType<typeof createGuestAnalytics>;
